import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	ConfluenceLogo,
	JiraLogo,
	JiraProductDiscoveryLogo,
	JiraServiceManagementLogo,
	JiraServiceManagementIcon,
} from '@atlaskit/logo';

import { AtlassianMarkBrandV3 as AtlassianMarkBrandV3Logo } from '@/libraries/wpl/foundations/src/logos-v3/atlassian-v3-mark-brand';
import { getEnvironment } from '@/utils/environment';

import { RovoDevAgentsLogo } from './logos/rovo-dev-agents-logo';

export enum ProductKey {
	JIRA_SOFTWARE = 'jira-software.ondemand',
	BITBUCKET = 'com.atlassian.bitbucket',
	CONFLUENCE = 'confluence.ondemand',
	OPSGENIE = 'opsgenie',
	JIRA_SERVICE_DESK = 'jira-servicedesk.ondemand',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- intentional alias of JIRA_SERVICE_DESK
	JIRA_SERVICE_MANAGEMENT = 'jira-servicedesk.ondemand',
	JIRA_CORE = 'jira-core.ondemand',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- intentional alias of JIRA_CORE
	JIRA_WORK_MANAGEMENT = 'jira-core.ondemand',
	ACCESS = 'com.atlassian.identity-manager',
	STATUSPAGE = 'statuspage',
	CONFLUENCE_PLUGIN_QUESTIONS = 'com.atlassian.confluence.plugins.confluence-questions.ondemand',
	CONFLUENCE_TEAM_CALENDARS = 'team.calendars.confluence.ondemand',
	JIRA_ROADMAPS = 'com.radiantminds.roadmaps-jira.ondemand',
	JIRA_BONFIRE = 'bonfire.jira.ondemand',
	JIRA_REFAPP = 'refapp',
	TEAM_CENTRAL = 'townsquare',
	JIRA_PRODUCT_DISCOVERY = 'jira-product-discovery',
	COMPASS = 'compass',
	// eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- intentional alias of TEAM_CENTRAL
	ATLAS = 'townsquare',
	FABRIC = 'atlassian-whiteboard',
	BEACON = 'beacon',
	LOOM = 'loom',
	ROVO = 'rovo.ondemand',
	DEVAI = 'devai.ondemand',
	CUSTOMER_MANAGED_KEYS = 'com.atlassian.customer-managed-keys',
	JIRA_CUSTOMER_SERVICE = 'jira-customer-service',
	TEAMWORK_COLLECTION = 'com.atlassian.teamwork-collection',
	SERVICE_COLLECTION = 'com.atlassian.service-collection',
	STRATEGY_COLLECTION = 'com.atlassian.strategy-collection',
}

export const getProductData = (productKey: ProductKey) => {
	if (productKey === ProductKey.SERVICE_COLLECTION) {
		return ProductData[ProductKey.JIRA_SERVICE_DESK];
	}
	if (productKey in ProductData) {
		return ProductData[productKey as keyof typeof ProductData];
	}
	return ProductData[ProductKey.JIRA_SOFTWARE];
};

export type ProductDataDetail = {
	productKey: ProductKey;
	productName: string;
	productLogo:
		| typeof JiraLogo
		| typeof ConfluenceLogo
		| typeof JiraServiceManagementLogo
		| typeof RovoDevAgentsLogo
		| typeof AtlassianMarkBrandV3Logo;
};

enum PricingType {
	FREE = 'FREE',
	PAID = 'PAID',
	LIMITED_FREE = 'LIMITED_FREE',
}

export const bundles = [
	'beacon',
	'bitbucket',
	'compass',
	'confluence',
	'confluence-jira-service-management',
	'confluence-jira-software',
	'developer',
	'fabric',
	'jira',
	'jira-product-discovery',
	'jira-service-management',
	'jira-service-management-confluence',
	'jira-service-management-jira-software',
	'jira-service-management-jira-software-confluence',
	'jira-software',
	'jira-software-confluence',
	'jira-software-confluence-compass',
	'jira-software-jira-service-management',
	'jira-software-jira-service-management-confluence',
	'loom',
	'opsgenie',
	'statuspage',
	'devai',
	'service-collection',
	'teamwork-collection',
	'strategy-collection',
] as const;

export type Bundle = (typeof bundles)[number];

export const editions = ['free', 'standard', 'premium'] as const;

export type Edition = (typeof editions)[number];

export type EnvironmentType = keyof typeof OFFERING_IDS;

export type CcpProduct = {
	readonly offeringId: string;
	readonly pricingPlanId: string;
	readonly pricingType: PricingType;
	readonly productKey: string;
	readonly edition: string;
	readonly chargeElement: string;
	readonly childProductsForPriorityActivation?: string[];
};

export const ProductData = {
	[ProductKey.CONFLUENCE]: {
		productKey: ProductKey.CONFLUENCE,
		productName: 'Confluence',
		productLogo: ConfluenceLogo,
	},
	[ProductKey.DEVAI]: {
		productKey: ProductKey.DEVAI,
		productName: 'Rovo Dev Agents',
		productLogo: RovoDevAgentsLogo, // Placeholder, replace with actual Dev AI logo when available
	},
	[ProductKey.JIRA_SERVICE_DESK]: {
		productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
		productName: 'Jira Service Management',
		productLogo: JiraServiceManagementLogo,
	},
	[ProductKey.JIRA_SOFTWARE]: {
		productKey: ProductKey.JIRA_SOFTWARE,
		productName: 'Jira',
		productLogo: JiraLogo,
	},
	[ProductKey.TEAMWORK_COLLECTION]: {
		productKey: ProductKey.TEAMWORK_COLLECTION,
		productName: 'Teamwork Collection',
		productLogo: JiraLogo, // Placeholder - can be updated when Teamwork Collection logo is available
	},
	[ProductKey.SERVICE_COLLECTION]: {
		productKey: ProductKey.SERVICE_COLLECTION,
		productName: 'Service Collection',
		productLogo: JiraLogo,
	},
	[ProductKey.STRATEGY_COLLECTION]: {
		productKey: ProductKey.STRATEGY_COLLECTION,
		productName: 'Strategy Collection',
		productLogo: JiraLogo,
	},
	[ProductKey.JIRA_PRODUCT_DISCOVERY]: {
		productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
		productName: 'Jira Product Discovery',
		productLogo: JiraProductDiscoveryLogo,
	},
	[ProductKey.JIRA_CUSTOMER_SERVICE]: {
		productKey: ProductKey.JIRA_CUSTOMER_SERVICE,
		productName: 'Customer Service Management',
		productLogo: JiraServiceManagementIcon,
	},
};

const OFFERING_IDS = {
	dev: {
		[ProductKey.JIRA_SOFTWARE]: {
			free: {
				offeringId: 'de2887c9-8a55-41d5-b5cf-ad6a5589ebed',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: 'a70b5cbb-1ae1-4003-8f4a-9001a4a50526',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '6dd805b4-da75-4374-a7a7-cf0b12f7ea07',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.CONFLUENCE]: {
			free: {
				offeringId: '7547cfd6-a804-4e42-8bed-e656d49ea09e',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '2eda1119-fab4-4699-ab37-ff8f12052cde',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '06ef245c-5fa3-473b-8822-5e6fe072ae4b',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.JIRA_SERVICE_MANAGEMENT]: {
			free: {
				offeringId: '2bad3c2f-d740-44a9-b08d-849202f11830',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'agent',
			},
			standard: {
				offeringId: '396efb16-749b-41fa-9cd1-ad10cfefa1d0',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'agent',
			},
			premium: {
				offeringId: '0c6683b4-4633-4673-beca-690d171de6a5',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'agent',
			},
			halp: {
				offeringId: `396efb16-749b-41fa-9cd1-ad10cfefa1d0`, // https://workbench.staging.atl-paas.net/offerings/396efb16-749b-41fa-9cd1-ad10cfefa1d0
				pricingPlanId: '7701f274-6c84-45cd-bdb0-493a67355e61', // https://workbench.staging.atl-paas.net/products/jira-servicedesk.ondemand/plans/7701f274-6c84-45cd-bdb0-493a67355e61
				pricingType: PricingType.PAID, // pricing type for CCP
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				edition: 'standard',
				chargeElement: 'agent',
			},
		},
		[ProductKey.JIRA_WORK_MANAGEMENT]: {
			free: {
				offeringId: '769055e8-05f5-4b1a-ba1a-a1afc874ee6d',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '5ab35ef5-e441-493a-b6e0-ba2d21a72ac6',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'd8e9a61a-c83b-4e40-a757-997d5bae2bb2',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.JIRA_PRODUCT_DISCOVERY]: {
			free: {
				offeringId: 'd843dd9e-23c5-4d27-af44-5009d568cb6e',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '35b9268e-66e2-45fb-81d3-dacd1e31831a',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'f0b24ca7-c3d8-4cac-b5ed-60f4152d4dcd',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.COMPASS]: {
			beta: {
				offeringId: '7af07cb3-d4e5-473e-87b3-3ae3bc9248a6',
				productKey: ProductKey.COMPASS,
				edition: 'beta',
				chargeElement: 'user',
			},
			free: {
				offeringId: '1d1951e5-881c-4f75-b0cd-3c6859dd5759',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '575abf20-6cac-4bbc-9666-1791fae8d822',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '4b41c4e3-7f26-4182-a12b-8ed7468350c4',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.ATLAS]: {
			free: {
				offeringId: '88064fa1-b573-4358-a9de-ec350aac1ddc',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '01c8ddad-8ea4-4da9-8127-71d6ff6de1c9',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'f7866043-d6e4-4d54-9c6a-4f82c6a1ebe5',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.OPSGENIE]: {
			free: {
				offeringId: 'd0c6ab1d-d8ea-49f8-8893-eafca2e05158',
				productKey: ProductKey.OPSGENIE,
				pricingType: PricingType.FREE,
				edition: 'essentials',
				chargeElement: 'user',
			},
		},
		[ProductKey.FABRIC]: {
			free: {
				offeringId: 'b0c7b413-b264-4c01-8b67-c61aa2c3e455',
				productKey: ProductKey.FABRIC,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
		},
		[ProductKey.STATUSPAGE]: {
			free: {
				offeringId: 'cff16c60-539b-11ea-8d77-2e728ce88125',
				productKey: ProductKey.STATUSPAGE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
		},
		[ProductKey.BITBUCKET]: {
			free: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.DEVAI]: {
			free: {
				offeringId: 'd69d4faa-7677-48f2-a432-fda525d2e223',
				productKey: ProductKey.DEVAI,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '7618c3e4-2e84-4a47-b322-d2515835c55a',
				productKey: ProductKey.DEVAI,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
		},
		[ProductKey.SERVICE_COLLECTION]: {
			free: {
				offeringId: '9705f74d-d4af-48e1-b443-94a8ae9dff54',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '3e6a7923-6df0-4ab6-b819-2168e8decef3',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'agent',
			},
			premium: {
				offeringId: '24698695-2f89-495b-b266-109b3f5c2210',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'agent',
			},
		},
		[ProductKey.TEAMWORK_COLLECTION]: {
			free: {
				offeringId: '666dec82-def0-4675-8abe-171d26c968a0',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '7e16cb96-ade2-4220-9010-c71ef9a70cf2',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'b296663a-50d7-4e51-bc53-be058da00dad',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.LOOM]: {
			free: {
				offeringId: '67737b67-caad-48af-ac36-01a64e9f220b',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: 'd009c283-d868-4d90-aa1e-17609d515f0f',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.PAID,
				edition: 'business',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '3f72cea3-aa1c-446c-a3e4-d09fed86a62c',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.PAID,
				edition: 'business-ai',
				chargeElement: 'user',
			},
		},
	},
	staging: {
		[ProductKey.JIRA_SOFTWARE]: {
			free: {
				offeringId: 'de2887c9-8a55-41d5-b5cf-ad6a5589ebed',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
			standard: {
				offeringId: 'a70b5cbb-1ae1-4003-8f4a-9001a4a50526',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
			premium: {
				offeringId: '6dd805b4-da75-4374-a7a7-cf0b12f7ea07',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
		},
		[ProductKey.CONFLUENCE]: {
			free: {
				offeringId: '7547cfd6-a804-4e42-8bed-e656d49ea09e',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '2eda1119-fab4-4699-ab37-ff8f12052cde',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '06ef245c-5fa3-473b-8822-5e6fe072ae4b',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.JIRA_SERVICE_MANAGEMENT]: {
			free: {
				offeringId: '2bad3c2f-d740-44a9-b08d-849202f11830',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'agent',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
			standard: {
				offeringId: '396efb16-749b-41fa-9cd1-ad10cfefa1d0',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'agent',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
			premium: {
				offeringId: '0c6683b4-4633-4673-beca-690d171de6a5',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'agent',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
			halp: {
				offeringId: `396efb16-749b-41fa-9cd1-ad10cfefa1d0`, // https://workbench.staging.atl-paas.net/offerings/396efb16-749b-41fa-9cd1-ad10cfefa1d0
				pricingPlanId: '7701f274-6c84-45cd-bdb0-493a67355e61', // https://workbench.staging.atl-paas.net/products/jira-servicedesk.ondemand/plans/7701f274-6c84-45cd-bdb0-493a67355e61
				pricingType: PricingType.PAID, // pricing type for CCP
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				edition: 'standard',
				chargeElement: 'agent',
			},
		},
		[ProductKey.JIRA_WORK_MANAGEMENT]: {
			free: {
				offeringId: '769055e8-05f5-4b1a-ba1a-a1afc874ee6d',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
			standard: {
				offeringId: '5ab35ef5-e441-493a-b6e0-ba2d21a72ac6',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
				jiraFamilyOfferingId: '009f8789-c10c-4366-9420-f712d02a6499',
			},
			premium: {
				offeringId: 'd8e9a61a-c83b-4e40-a757-997d5bae2bb2',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.JIRA_PRODUCT_DISCOVERY]: {
			free: {
				offeringId: 'd843dd9e-23c5-4d27-af44-5009d568cb6e',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '35b9268e-66e2-45fb-81d3-dacd1e31831a',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'f0b24ca7-c3d8-4cac-b5ed-60f4152d4dcd',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.COMPASS]: {
			beta: {
				offeringId: '7af07cb3-d4e5-473e-87b3-3ae3bc9248a6',
				productKey: ProductKey.COMPASS,
				edition: 'beta',
				chargeElement: 'user',
			},
			free: {
				offeringId: '1d1951e5-881c-4f75-b0cd-3c6859dd5759',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '575abf20-6cac-4bbc-9666-1791fae8d822',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '4b41c4e3-7f26-4182-a12b-8ed7468350c4',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.ATLAS]: {
			free: {
				offeringId: '88064fa1-b573-4358-a9de-ec350aac1ddc',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '01c8ddad-8ea4-4da9-8127-71d6ff6de1c9',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'f7866043-d6e4-4d54-9c6a-4f82c6a1ebe5',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.OPSGENIE]: {
			free: {
				offeringId: 'd0c6ab1d-d8ea-49f8-8893-eafca2e05158',
				productKey: ProductKey.OPSGENIE,
				pricingType: PricingType.FREE,
				edition: 'essentials',
				chargeElement: 'user',
			},
		},
		[ProductKey.FABRIC]: {
			free: {
				offeringId: 'b0c7b413-b264-4c01-8b67-c61aa2c3e455',
				productKey: ProductKey.FABRIC,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
		},
		[ProductKey.STATUSPAGE]: {
			free: {
				offeringId: 'cff16c60-539b-11ea-8d77-2e728ce88125',
				productKey: ProductKey.STATUSPAGE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
		},
		[ProductKey.BITBUCKET]: {
			free: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.DEVAI]: {
			free: {
				offeringId: 'd69d4faa-7677-48f2-a432-fda525d2e223',
				productKey: ProductKey.DEVAI,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '7618c3e4-2e84-4a47-b322-d2515835c55a',
				productKey: ProductKey.DEVAI,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
		},
		[ProductKey.CUSTOMER_MANAGED_KEYS]: {
			standard: {
				offeringId: 'c8a6f3e4-0134-4cd3-9d23-63d834651c6b',
				productKey: ProductKey.CUSTOMER_MANAGED_KEYS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
		},
		[ProductKey.SERVICE_COLLECTION]: {
			free: {
				offeringId: '9705f74d-d4af-48e1-b443-94a8ae9dff54',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '3e6a7923-6df0-4ab6-b819-2168e8decef3',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'agent',
			},
			premium: {
				offeringId: '24698695-2f89-495b-b266-109b3f5c2210',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'agent',
			},
		},
		[ProductKey.TEAMWORK_COLLECTION]: {
			free: {
				offeringId: '666dec82-def0-4675-8abe-171d26c968a0',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '7e16cb96-ade2-4220-9010-c71ef9a70cf2',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'b296663a-50d7-4e51-bc53-be058da00dad',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.LOOM]: {
			free: {
				offeringId: '67737b67-caad-48af-ac36-01a64e9f220b',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: 'd009c283-d868-4d90-aa1e-17609d515f0f',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.PAID,
				edition: 'business',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '3f72cea3-aa1c-446c-a3e4-d09fed86a62c',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.PAID,
				edition: 'business-ai',
				chargeElement: 'user',
			},
		},
	},
	production: {
		[ProductKey.JIRA_SOFTWARE]: {
			free: {
				offeringId: 'de2887c9-8a55-41d5-b5cf-ad6a5589ebed',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: 'a70b5cbb-1ae1-4003-8f4a-9001a4a50526',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '6dd805b4-da75-4374-a7a7-cf0b12f7ea07',
				productKey: ProductKey.JIRA_SOFTWARE,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.CONFLUENCE]: {
			free: {
				offeringId: '7547cfd6-a804-4e42-8bed-e656d49ea09e',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '2eda1119-fab4-4699-ab37-ff8f12052cde',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '06ef245c-5fa3-473b-8822-5e6fe072ae4b',
				productKey: ProductKey.CONFLUENCE,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.JIRA_SERVICE_MANAGEMENT]: {
			free: {
				offeringId: '2bad3c2f-d740-44a9-b08d-849202f11830',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'agent',
			},
			standard: {
				offeringId: '396efb16-749b-41fa-9cd1-ad10cfefa1d0',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'agent',
			},
			premium: {
				offeringId: '0c6683b4-4633-4673-beca-690d171de6a5',
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'agent',
			},
			halp: {
				offeringId: `396efb16-749b-41fa-9cd1-ad10cfefa1d0`,
				pricingPlanId: '200b2088-934b-4669-b7c5-7ae6961c9368', // https://workbench.prod.atl-paas.net/products/jira-servicedesk.ondemand/plans/200b2088-934b-4669-b7c5-7ae6961c9368
				pricingType: PricingType.PAID, // pricing type for CCP
				productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
				edition: 'standard',
				chargeElement: 'agent',
			},
		},
		[ProductKey.JIRA_WORK_MANAGEMENT]: {
			free: {
				offeringId: '769055e8-05f5-4b1a-ba1a-a1afc874ee6d',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '5ab35ef5-e441-493a-b6e0-ba2d21a72ac6',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'd8e9a61a-c83b-4e40-a757-997d5bae2bb2',
				productKey: ProductKey.JIRA_WORK_MANAGEMENT,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.JIRA_PRODUCT_DISCOVERY]: {
			free: {
				offeringId: 'd843dd9e-23c5-4d27-af44-5009d568cb6e',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '35b9268e-66e2-45fb-81d3-dacd1e31831a',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'f0b24ca7-c3d8-4cac-b5ed-60f4152d4dcd',
				productKey: ProductKey.JIRA_PRODUCT_DISCOVERY,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.COMPASS]: {
			beta: {
				offeringId: '7af07cb3-d4e5-473e-87b3-3ae3bc9248a6',
				productKey: 'compass',
				edition: 'beta',
				chargeElement: 'user',
			},
			free: {
				offeringId: '1d1951e5-881c-4f75-b0cd-3c6859dd5759',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '575abf20-6cac-4bbc-9666-1791fae8d822',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '4b41c4e3-7f26-4182-a12b-8ed7468350c4',
				productKey: ProductKey.COMPASS,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.ATLAS]: {
			free: {
				offeringId: '88064fa1-b573-4358-a9de-ec350aac1ddc',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '01c8ddad-8ea4-4da9-8127-71d6ff6de1c9',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'f7866043-d6e4-4d54-9c6a-4f82c6a1ebe5',
				productKey: ProductKey.ATLAS,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.OPSGENIE]: {
			free: {
				offeringId: 'd0c6ab1d-d8ea-49f8-8893-eafca2e05158',
				productKey: ProductKey.OPSGENIE,
				pricingType: PricingType.FREE,
				edition: 'essentials',
				chargeElement: 'user',
			},
		},
		[ProductKey.FABRIC]: {
			free: {
				offeringId: 'b0c7b413-b264-4c01-8b67-c61aa2c3e455',
				productKey: ProductKey.FABRIC,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
		},
		[ProductKey.STATUSPAGE]: {
			free: {
				offeringId: 'cff16c60-539b-11ea-8d77-2e728ce88125',
				productKey: ProductKey.STATUSPAGE,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
		},
		[ProductKey.BITBUCKET]: {
			free: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '00000000-0000-0000-0000-000000000000',
				productKey: ProductKey.BITBUCKET,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.DEVAI]: {
			free: {
				offeringId: 'd69d4faa-7677-48f2-a432-fda525d2e223',
				productKey: ProductKey.DEVAI,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '7618c3e4-2e84-4a47-b322-d2515835c55a',
				productKey: ProductKey.DEVAI,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
		},
		[ProductKey.CUSTOMER_MANAGED_KEYS]: {
			standard: {
				offeringId: 'c8a6f3e4-0134-4cd3-9d23-63d834651c6b',
				productKey: ProductKey.CUSTOMER_MANAGED_KEYS,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
		},
		[ProductKey.SERVICE_COLLECTION]: {
			free: {
				offeringId: '9705f74d-d4af-48e1-b443-94a8ae9dff54',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '3e6a7923-6df0-4ab6-b819-2168e8decef3',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'agent',
			},
			premium: {
				offeringId: '24698695-2f89-495b-b266-109b3f5c2210',
				productKey: ProductKey.SERVICE_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'agent',
			},
		},
		[ProductKey.TEAMWORK_COLLECTION]: {
			free: {
				offeringId: '666dec82-def0-4675-8abe-171d26c968a0',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: '7e16cb96-ade2-4220-9010-c71ef9a70cf2',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'standard',
				chargeElement: 'user',
			},
			premium: {
				offeringId: 'b296663a-50d7-4e51-bc53-be058da00dad',
				productKey: ProductKey.TEAMWORK_COLLECTION,
				pricingType: PricingType.PAID,
				edition: 'premium',
				chargeElement: 'user',
			},
		},
		[ProductKey.LOOM]: {
			free: {
				offeringId: '67737b67-caad-48af-ac36-01a64e9f220b',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.FREE,
				edition: 'free',
				chargeElement: 'user',
			},
			standard: {
				offeringId: 'd009c283-d868-4d90-aa1e-17609d515f0f',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.PAID,
				edition: 'business',
				chargeElement: 'user',
			},
			premium: {
				offeringId: '3f72cea3-aa1c-446c-a3e4-d09fed86a62c',
				productKey: ProductKey.LOOM,
				pricingType: PricingType.PAID,
				edition: 'business-ai',
				chargeElement: 'user',
			},
		},
	},
} as const;

export enum OrgBasedProduct {
	GUARD_PREMIUM = 'GUARD_PREMIUM',
	GUARD_PREMIUM_DEMO = 'GUARD_PREMIUM_DEMO',
	GUARD_STANDARD = 'GUARD_STANDARD',
	ROVO_ORG = 'ROVO_ORG',
	ROVO_ORG_DEMO = 'ROVO_ORG_DEMO',
	ROVO_ORG_FREE = 'ROVO_ORG_FREE',
	ROVO_CREDITS_PACK = 'ROVO_CREDITS_PACK',
	ASSET_CREDITS_PACK = 'ASSET_CREDITS_PACK',
	AUTOMATION_CREDITS_PACK = 'AUTOMATION_CREDITS_PACK',
	CS_RESOLUTION_CREDITS_PACK = 'CS_RESOLUTION_CREDITS_PACK',
	BITBUCKET_LFS_STORAGE_CREDITS_PACK = 'BITBUCKET_LFS_STORAGE_CREDITS_PACK',
	BITBUCKET_BUILD_TIME_CREDITS_PACK = 'BITBUCKET_BUILD_TIME_CREDITS_PACK',
	BITBUCKET_PACKAGE_STORAGE_CREDITS_PACK = 'BITBUCKET_PACKAGE_STORAGE_CREDITS_PACK',
	BITBUCKET_PACKAGE_NETWORK_CREDITS_PACK = 'BITBUCKET_PACKAGE_NETWORK_CREDITS_PACK',
	INDEXED_OBJECTS_PACK = 'INDEXED_OBJECTS_PACK',
	TWC_ORG_FREE = 'TWC_ORG_FREE',
	TWC_ORG_STANDARD = 'TWC_ORG_STANDARD',
	TWC_ORG_PREMIUM = 'TWC_ORG_PREMIUM',
	TWC_ORG_PREMIUM_BILLED_SEPARATELY = 'TWC_ORG_PREMIUM_BILLED_SEPARATELY',
	TWC_ORG_ENTERPRISE = 'TWC_ORG_ENTERPRISE',
	STRATEGY_ORG_ENTERPRISE = 'STRATEGY_ORG_ENTERPRISE',
	CUSTOMER_MANAGED_KEYS = 'CUSTOMER_MANAGED_KEYS',
	CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY = 'CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY',
	SERVICE_COLLECTION_ORG_FREE = 'SERVICE_COLLECTION_ORG_FREE',
	SERVICE_COLLECTION_ORG_STANDARD = 'SERVICE_COLLECTION_ORG_STANDARD',
	SERVICE_COLLECTION_ORG_PREMIUM = 'SERVICE_COLLECTION_ORG_PREMIUM',
	SERVICE_COLLECTION_ORG_ENTERPRISE = 'SERVICE_COLLECTION_ORG_ENTERPRISE',
}

export const CMK_SUPPORT_URL =
	'https://support.atlassian.com/security-and-access-policies/docs/set-up-cmk-enabled-atlassian-apps/';

export const ORG_BASED_OFFERING_IDS = {
	GUARD_PREMIUM: '2f5e5808-d501-450a-b246-b0bd50a0b72a',
	GUARD_PREMIUM_DEMO: '2081c1b7-2ad6-3e40-a7c2-5a7f876a38a7',
	GUARD_STANDARD: '1ac3b7dd-7141-414e-bc00-c890acc458b0',
	ROVO_ORG: 'e7de08be-3d20-423e-b928-dd1fd325da7a',
	ROVO_ORG_DEMO: 'df531ba8-186a-3924-96a3-23056fa96f3f',
	ROVO_ORG_FREE: '71f2d378-1960-4336-8e7b-6af35174650c',
	ROVO_CREDITS_PACK: '918c0980-3992-4e57-a76e-0bde1c94e435',
	ASSET_CREDITS_PACK: '36a69b0a-bf2f-4d40-bb95-6b2a40d52718',
	AUTOMATION_CREDITS_PACK: 'bf680870-2670-4487-8ba3-ba6c9c49eb63',
	CS_RESOLUTION_CREDITS_PACK: '2b89e53d-62e7-42da-9174-9177ad961054',
	BITBUCKET_LFS_STORAGE_CREDITS_PACK: '3912fe8d-5b2e-4c38-a3d8-84552ca5b9d0',
	BITBUCKET_BUILD_TIME_CREDITS_PACK: '6e224a30-1516-49c9-ae19-69c803f25df4',
	BITBUCKET_PACKAGE_STORAGE_CREDITS_PACK: '050ca109-c952-45bf-ace9-3ecea0db8752',
	BITBUCKET_PACKAGE_NETWORK_CREDITS_PACK: '2cbd0691-5925-43e6-93d8-da14e2570ee9',
	INDEXED_OBJECTS_PACK: '083e1434-9d6a-4478-8534-7e586038775c',
	TWC_ORG_STANDARD: '7e16cb96-ade2-4220-9010-c71ef9a70cf2',
	TWC_ORG_PREMIUM: 'b296663a-50d7-4e51-bc53-be058da00dad',
	TWC_ORG_PREMIUM_BILLED_SEPARATELY: '17c64528-3ce0-3ce5-8d61-26726b323416',
	TWC_ORG_ENTERPRISE: 'cf0b0103-2f08-4c87-97cf-a69448d6e69a',
	TWC_ORG_FREE: '666dec82-def0-4675-8abe-171d26c968a0',
	STRATEGY_ORG_ENTERPRISE: 'b7da0afa-6941-4010-9c29-cc8739f6fbf8',
	CUSTOMER_MANAGED_KEYS: 'c8a6f3e4-0134-4cd3-9d23-63d834651c6b',
	CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY: '742112ad-8294-302d-bcab-b08362e15dee',
	SERVICE_COLLECTION_ORG_FREE: '9705f74d-d4af-48e1-b443-94a8ae9dff54',
	SERVICE_COLLECTION_ORG_STANDARD: '3e6a7923-6df0-4ab6-b819-2168e8decef3',
	SERVICE_COLLECTION_ORG_PREMIUM: '24698695-2f89-495b-b266-109b3f5c2210',
	SERVICE_COLLECTION_ORG_ENTERPRISE: 'c1b6f7ce-21f9-4e85-b136-91f4c484b95d',
};

export const CMK_OFFERING_IDS = [
	ORG_BASED_OFFERING_IDS.CUSTOMER_MANAGED_KEYS,
	ORG_BASED_OFFERING_IDS.CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY,
];

export const collectionOfferingIds = new Set<string>([
	ORG_BASED_OFFERING_IDS.TWC_ORG_FREE,
	ORG_BASED_OFFERING_IDS.TWC_ORG_STANDARD,
	ORG_BASED_OFFERING_IDS.TWC_ORG_PREMIUM,
	ORG_BASED_OFFERING_IDS.TWC_ORG_PREMIUM_BILLED_SEPARATELY,
	ORG_BASED_OFFERING_IDS.TWC_ORG_ENTERPRISE,
	ORG_BASED_OFFERING_IDS.STRATEGY_ORG_ENTERPRISE,
	ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_FREE,
	ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_STANDARD,
	ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_PREMIUM,
	ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_ENTERPRISE,
]);

export const orgBasedOfferingIdMap = {
	[ORG_BASED_OFFERING_IDS.GUARD_PREMIUM]: OrgBasedProduct.GUARD_PREMIUM,
	[ORG_BASED_OFFERING_IDS.GUARD_PREMIUM_DEMO]: OrgBasedProduct.GUARD_PREMIUM_DEMO,
	[ORG_BASED_OFFERING_IDS.GUARD_STANDARD]: OrgBasedProduct.GUARD_STANDARD,
	[ORG_BASED_OFFERING_IDS.ROVO_ORG]: OrgBasedProduct.ROVO_ORG,
	[ORG_BASED_OFFERING_IDS.ROVO_ORG_DEMO]: OrgBasedProduct.ROVO_ORG_DEMO,
	[ORG_BASED_OFFERING_IDS.ROVO_ORG_FREE]: OrgBasedProduct.ROVO_ORG_FREE,
	[ORG_BASED_OFFERING_IDS.ROVO_CREDITS_PACK]: OrgBasedProduct.ROVO_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.ASSET_CREDITS_PACK]: OrgBasedProduct.ASSET_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.AUTOMATION_CREDITS_PACK]: OrgBasedProduct.AUTOMATION_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.CS_RESOLUTION_CREDITS_PACK]: OrgBasedProduct.CS_RESOLUTION_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.BITBUCKET_LFS_STORAGE_CREDITS_PACK]:
		OrgBasedProduct.BITBUCKET_LFS_STORAGE_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.BITBUCKET_BUILD_TIME_CREDITS_PACK]:
		OrgBasedProduct.BITBUCKET_BUILD_TIME_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.BITBUCKET_PACKAGE_STORAGE_CREDITS_PACK]:
		OrgBasedProduct.BITBUCKET_PACKAGE_STORAGE_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.BITBUCKET_PACKAGE_NETWORK_CREDITS_PACK]:
		OrgBasedProduct.BITBUCKET_PACKAGE_NETWORK_CREDITS_PACK,
	[ORG_BASED_OFFERING_IDS.INDEXED_OBJECTS_PACK]: OrgBasedProduct.INDEXED_OBJECTS_PACK,
	[ORG_BASED_OFFERING_IDS.TWC_ORG_FREE]: OrgBasedProduct.TWC_ORG_FREE,
	[ORG_BASED_OFFERING_IDS.TWC_ORG_STANDARD]: OrgBasedProduct.TWC_ORG_STANDARD,
	[ORG_BASED_OFFERING_IDS.TWC_ORG_PREMIUM]: OrgBasedProduct.TWC_ORG_PREMIUM,
	[ORG_BASED_OFFERING_IDS.TWC_ORG_PREMIUM_BILLED_SEPARATELY]:
		OrgBasedProduct.TWC_ORG_PREMIUM_BILLED_SEPARATELY,
	[ORG_BASED_OFFERING_IDS.TWC_ORG_ENTERPRISE]: OrgBasedProduct.TWC_ORG_ENTERPRISE,
	[ORG_BASED_OFFERING_IDS.STRATEGY_ORG_ENTERPRISE]: OrgBasedProduct.STRATEGY_ORG_ENTERPRISE,
	[ORG_BASED_OFFERING_IDS.CUSTOMER_MANAGED_KEYS]: OrgBasedProduct.CUSTOMER_MANAGED_KEYS,
	[ORG_BASED_OFFERING_IDS.CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY]:
		OrgBasedProduct.CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY,
	[ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_FREE]: OrgBasedProduct.SERVICE_COLLECTION_ORG_FREE,
	[ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_STANDARD]:
		OrgBasedProduct.SERVICE_COLLECTION_ORG_STANDARD,
	[ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_PREMIUM]:
		OrgBasedProduct.SERVICE_COLLECTION_ORG_PREMIUM,
	[ORG_BASED_OFFERING_IDS.SERVICE_COLLECTION_ORG_ENTERPRISE]:
		OrgBasedProduct.SERVICE_COLLECTION_ORG_ENTERPRISE,
};

export const mapProductKeyAndEditionToCcpProduct = ({
	productKey,
	edition,
	primaryCollectionProduct,
}: {
	productKey: ProductKey;
	edition: Edition;
	primaryCollectionProduct?: ProductKey | null;
}): CcpProduct => {
	const products = OFFERING_IDS[getEnvironment()];
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- referenced as a type via `keyof typeof editions`
	const editions = OFFERING_IDS[getEnvironment()][productKey as keyof typeof products];

	const ccpProduct = products[productKey as keyof typeof products][
		edition as keyof typeof editions
	] as CcpProduct;

	// Update JSM standard offeringId to the new one if feature gate is enabled
	// TODO: Remove this once successfully migrated to use the new offeringId
	if (ccpProduct.offeringId === '396efb16-749b-41fa-9cd1-ad10cfefa1d0') {
		const isNewJSMOfferingIdEnabled = FeatureGates.checkGate(
			'signup_update_jsm_standard_offeringid',
		);

		if (isNewJSMOfferingIdEnabled) {
			const newCcpProduct = {
				...ccpProduct,
				offeringId: '99bcbf6c-4fff-4a45-8303-a854e7fbd17c',
			};
			return newCcpProduct;
		}
	} else if (ccpProduct.offeringId === 'd843dd9e-23c5-4d27-af44-5009d568cb6e') {
		// JPD Free
		const isNewJPDOfferingIdEnabled = FeatureGates.checkGate('signup_update_jpd_offeringids');

		if (isNewJPDOfferingIdEnabled) {
			const newCcpProduct = {
				...ccpProduct,
				offeringId: 'd2b66d44-0754-4c89-8d3b-c8f9a8102184',
			};
			return newCcpProduct;
		}
	} else if (ccpProduct.offeringId === '35b9268e-66e2-45fb-81d3-dacd1e31831a') {
		// JPD Standard
		const isNewJPDOfferingIdEnabled = FeatureGates.checkGate('signup_update_jpd_offeringids');

		if (isNewJPDOfferingIdEnabled) {
			const newCcpProduct = {
				...ccpProduct,
				offeringId: 'cb8bc8af-491f-41be-8502-ae5de94d04f0',
			};
			return newCcpProduct;
		}
	}

	// service collections with a primaryCollectionProduct prioritize that product first
	const childProductsForPriorityActivation =
		productKey === ProductKey.SERVICE_COLLECTION && primaryCollectionProduct
			? [primaryCollectionProduct]
			: [];

	if (childProductsForPriorityActivation.length > 0) {
		return {
			...ccpProduct,
			childProductsForPriorityActivation,
		};
	}

	return ccpProduct;
};

export const mapOfferingIdToProductKeyAndEdition = (
	offeringId: string,
): { productKey: ProductKey; edition: string } | null => {
	const products = OFFERING_IDS[getEnvironment()];

	// Search through all productKeys and editions to find the matching offeringId
	for (const productKey in products) {
		const productEditions = products[productKey as keyof typeof products] as Record<
			string,
			{ offeringId: string; productKey: string; edition: string }
		>;
		if (!productEditions) {
			continue;
		}

		for (const edition in productEditions) {
			const ccpProduct = productEditions[edition] as {
				offeringId: string;
				productKey: string;
				edition: string;
			};

			if (ccpProduct.offeringId === offeringId) {
				return {
					productKey: ccpProduct.productKey as ProductKey,
					edition: ccpProduct.edition,
				};
			}
		}
	}

	return null;
};

export const DefaultCollectionProductMap: Record<string, Partial<ProductKey>> = {
	[ProductKey.TEAMWORK_COLLECTION]: ProductKey.JIRA_SOFTWARE,
	[ProductKey.SERVICE_COLLECTION]: ProductKey.JIRA_SERVICE_MANAGEMENT,
};

export const CollectionProductKeyToARIStringMap: Record<string, string> = {
	[ProductKey.JIRA_SERVICE_MANAGEMENT]: 'jira-servicedesk',
	[ProductKey.JIRA_CUSTOMER_SERVICE]: 'jira-customer-service',
	[ProductKey.JIRA_SOFTWARE]: 'jira-software',
	[ProductKey.CONFLUENCE]: 'confluence',
	[ProductKey.LOOM]: 'loom',
};

export const CollectionConstituentProductsMap: Record<string, Array<string>> = {
	[ProductKey.SERVICE_COLLECTION]: [
		ProductKey.JIRA_SERVICE_MANAGEMENT,
		ProductKey.JIRA_CUSTOMER_SERVICE,
	],
	[ProductKey.TEAMWORK_COLLECTION]: [
		ProductKey.JIRA_SOFTWARE,
		ProductKey.CONFLUENCE,
		ProductKey.LOOM,
	],
};

export const CollectionBundleNameToProductKeyMap: Record<string, string> = {
	'service-collection': ProductKey.SERVICE_COLLECTION,
	'teamwork-collection': ProductKey.TEAMWORK_COLLECTION,
};

export const CollectionProductKeyToBundleNameMap: Record<string, string> = Object.entries(
	CollectionBundleNameToProductKeyMap,
).reduce(
	(acc, [bundleName, productKey]) => {
		acc[productKey] = bundleName;
		return acc;
	},
	{} as Record<string, string>,
);

export const TemplateKeys = {
	SimplifiedBasic: 'com.pyxis.greenhopper.jira:gh-simplified-basic',
	Scrum: 'com.pyxis.greenhopper.jira:gh-simplified-agility-scrum',
	Kanban: 'com.pyxis.greenhopper.jira:gh-simplified-agility-kanban',
	WebDesignProcess:
		'com.atlassian.jira-core-project-templates:jira-work-management-web-design-process',
	GoToMarket: 'com.atlassian.jira-core-project-templates:jira-work-management-go-to-market',
	SimplifiedProcessControl:
		'com.atlassian.jira-core-project-templates:jira-core-simplified-process-control',
	ProcessControlTeamManaged:
		'com.atlassian.jira-core-project-templates:jira-work-management-process-control-team-managed',
	SalesPipeline: 'com.atlassian.jira-core-project-templates:jira-work-management-sales-pipeline',
	SimplifiedProcurement:
		'com.atlassian.jira-core-project-templates:jira-core-simplified-procurement',
	EmployeeOnboarding:
		'com.atlassian.jira-core-project-templates:jira-work-management-employee-onboarding',
	PersonalTaskPlanner:
		'com.atlassian.jira-core-project-templates:jira-work-management-personal-task-planner',
	CampaignManagement:
		'com.atlassian.jira-core-project-templates:jira-work-management-campaign-management',
	CampaignManagementTeamManaged:
		'com.atlassian.jira-core-project-templates:jira-work-management-campaign-management-team-managed',
	CampaignManagementV2TeamManaged:
		'com.atlassian.jira-core-project-templates:jira-work-management-campaign-management-team-managed-v2',
	Recruitment: 'com.atlassian.jira-core-project-templates:jira-core-simplified-recruitment',
	RecruitmentTeamManaged:
		'com.atlassian.jira-core-project-templates:jira-work-management-recruitment-team-managed',
	LeadTracking: 'com.atlassian.jira-core-project-templates:jira-core-simplified-lead-tracking',
	LeadTrackingTeamManaged:
		'com.atlassian.jira-core-project-templates:jira-work-management-lead-tracking-team-managed',
	MonthEndClose: 'com.atlassian.jira-core-project-templates:jira-work-management-month-end-close',
	MonthEndCloseTeamManaged:
		'com.atlassian.jira-core-project-templates:jira-work-management-month-end-close-team-managed',
} as const;

export type TemplateKey = (typeof TemplateKeys)[keyof typeof TemplateKeys];
